/**
 * Offline-queue helpers used by every practice app to store save requests
 * that were made while the browser was offline, then flush them once we
 * reconnect.
 *
 * Each app gets its own set of localStorage keys via `createPendingStore`
 * (e.g. "qb_pendingGames", "wr_pendingGames") so one app's queue never
 * clobbers another's on the same origin.
 *
 * Migrations: earlier builds used unprefixed keys ("pendingGames"). Reads
 * transparently fall back to the legacy key and delete it once migrated.
 */

import type { AxiosRequestConfig } from "axios";
import libraryServices from "@workspace/shared/core/services/library/library.services";
import type {
  LibraryGame,
  SavePlaysPayload,
  SaveSettingsPayload,
} from "@workspace/shared/core/services/library/library.types";

type PendingKeys = {
  settings: string;
  games: string;
  plays: string;
  deletes: string;
  /** qb/st offline play-cell edits queued by the transformedPlays slice. */
  updatedPlays: string;
};

type LegacyKeys = {
  settings: string;
  games: string;
  plays: string;
};

const LEGACY_KEYS: LegacyKeys = {
  settings: "pendingSettings",
  games: "pendingGames",
  plays: "pendingPlays",
};

function safeRead<T>(key: string): T[] {
  const raw = localStorage.getItem(key);
  if (raw === null) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

/** Read prefixed key, falling back to legacy unprefixed key (and migrating). */
function readPending<T>(key: string, legacyKey: string): T[] {
  const prefixed = localStorage.getItem(key);
  if (prefixed !== null) return safeRead<T>(key);

  const legacy = localStorage.getItem(legacyKey);
  if (legacy === null) return [];
  try {
    const parsed = JSON.parse(legacy) as T[];
    localStorage.setItem(key, legacy);
    localStorage.removeItem(legacyKey);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export type PendingStore = {
  keys: PendingKeys;
  pushSettings: (settings: SaveSettingsPayload) => void;
  pushGame: (game: LibraryGame) => void;
  pushPlays: (payload: SavePlaysPayload) => void;
  /** Queue a play deletion that couldn't reach the backend (dedupes). */
  pushDelete: (playId: string | number) => void;
  readSettings: () => SaveSettingsPayload[];
  readGames: () => LibraryGame[];
  readPlays: () => SavePlaysPayload[];
  readDeletes: () => string[];
  clearSettings: () => void;
  clearGames: () => void;
  clearPlays: () => void;
  clearDeletes: () => void;
  /**
   * Push all queued saves to the backend. Order is deterministic:
   * settings → games → plays → deletes. Returns true if ANY items were successfully
   * flushed. Callers use this signal to trigger a games-list refresh so
   * offline-queued rows are reconciled with their server-assigned IDs
   * (otherwise the offline temp row + the new server row appear as a
   * duplicate until the user reloads).
   *
   * `config` is forwarded to every underlying request — the cross-app sweep
   * passes an explicit `x-app-selected` header so a drain routes to the right
   * app regardless of the shared global app key.
   */
  syncPending: (config?: AxiosRequestConfig) => Promise<boolean>;
};

/**
 * Build a per-app pending store. `appKey` becomes the localStorage prefix
 * (e.g. "qb" → "qb_pendingSettings"). The store methods automatically hit
 * shared libraryServices so all apps use the same endpoints.
 */
export function createPendingStore(appKey: string): PendingStore {
  const keys: PendingKeys = {
    settings: `${appKey}_pendingSettings`,
    games: `${appKey}_pendingGames`,
    plays: `${appKey}_pendingPlays`,
    deletes: `${appKey}_pendingDeletes`,
    // Written by the transformedPlays slice (pendingLocalStorageKey) — the
    // name must stay in lockstep with createTransformedPlaysSlice's option.
    updatedPlays: `${appKey}_pendingUpdatedPlays`,
  };

  const push = <T>(key: string, value: T) => {
    const current = safeRead<T>(key);
    current.push(value);
    localStorage.setItem(key, JSON.stringify(current));
  };

  const readSettings = () =>
    readPending<SaveSettingsPayload>(keys.settings, LEGACY_KEYS.settings);
  const readGames = () => readPending<LibraryGame>(keys.games, LEGACY_KEYS.games);
  const readPlays = () =>
    readPending<SavePlaysPayload>(keys.plays, LEGACY_KEYS.plays);
  // No legacy fallback — the deletes queue is new with this build.
  const readDeletes = () => safeRead<string>(keys.deletes);

  const clearSettings = () => localStorage.removeItem(keys.settings);
  const clearGames = () => localStorage.removeItem(keys.games);
  const clearPlays = () => localStorage.removeItem(keys.plays);
  const clearDeletes = () => localStorage.removeItem(keys.deletes);

  const pushDelete = (playId: string | number) => {
    const id = String(playId);
    const current = readDeletes();
    if (current.includes(id)) return;
    current.push(id);
    localStorage.setItem(keys.deletes, JSON.stringify(current));
  };

  const syncPending = async (
    config?: AxiosRequestConfig,
  ): Promise<boolean> => {
    const pendingSettings = readSettings();
    const pendingGames = readGames();
    const pendingPlays = readPlays();
    let anyFlushed = false;

    if (pendingSettings.length > 0) {
      for (const s of pendingSettings) {
        try {
          await libraryServices.onSaveSettings(s, config);
        } catch (err) {
          console.error(`[${appKey}] Failed to sync settings:`, err);
          return anyFlushed;
        }
      }
      clearSettings();
      anyFlushed = true;
    }

    if (pendingGames.length > 0) {
      for (const g of pendingGames) {
        try {
          const resp = await libraryServices.onSaveGame(g, config);
          // Offline-queued games carry their plays embedded, but the
          // backend's POST /games IGNORES inline plays — without this
          // follow-up POST the plays would be silently dropped and the
          // queue cleared, i.e. permanent data loss. POST /games returns
          // the raw entity (camelCase gameId); handle both shapes.
          const raw = resp?.data as
            | { game_id?: number; gameId?: number }
            | undefined;
          const gameId = raw?.game_id ?? raw?.gameId;
          if (Array.isArray(g.plays) && g.plays.length > 0) {
            if (gameId == null) {
              // The game POST succeeded but returned no resolvable id, so we
              // cannot attach its plays. DON'T fall through to clearGames()
              // and silently drop them — bail with the queue intact so a
              // later retry re-runs this game. (Preserving the coach's plays
              // outweighs a possible duplicate game row on the rare retry.)
              console.error(
                `[${appKey}] Saved game returned no game_id; keeping queue to avoid losing ${g.plays.length} play(s)`,
              );
              return anyFlushed;
            }
            await libraryServices.onSavePlays(
              {
                game_id: gameId,
                plays: g.plays,
              },
              config,
            );
          }
        } catch (err) {
          console.error(`[${appKey}] Failed to sync game:`, err);
          return anyFlushed;
        }
      }
      clearGames();
      anyFlushed = true;
    }

    // A payload the backend PERMANENTLY rejects (400 — e.g. a play that
    // belongs to another game) must not wedge the queue forever and block
    // everything behind it. Park it in a dead-letter key (data preserved
    // for recovery, never silently dropped) and keep flushing.
    const deadLetter = (kind: string, payload: unknown) => {
      try {
        const key = `${appKey}_deadLetter${kind}`;
        const current = safeRead<unknown>(key);
        current.push(payload);
        localStorage.setItem(key, JSON.stringify(current));
        console.error(
          `[${appKey}] Backend permanently rejected a ${kind} payload; parked at ${key}`,
        );
      } catch {
        /* quota — nothing more we can do */
      }
    };
    // 400 = payload the backend will never accept; 404 = the target game no
    // longer exists (deleted from another device). Neither can succeed by
    // retrying, and a stuck entry here blocked sign-out from EVERY app until
    // the coach chose "Log out anyway" — which wipes local data.
    const isPermanentRejection = (err: unknown): boolean => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      return status === 400 || status === 404;
    };

    if (pendingPlays.length > 0) {
      const remainingPlays = [...pendingPlays];
      for (const p of pendingPlays) {
        try {
          await libraryServices.onSavePlays(p, config);
          remainingPlays.splice(remainingPlays.indexOf(p), 1);
          anyFlushed = true;
        } catch (err) {
          if (isPermanentRejection(err)) {
            remainingPlays.splice(remainingPlays.indexOf(p), 1);
            deadLetter("Plays", p);
            continue;
          }
          console.error(`[${appKey}] Failed to sync plays:`, err);
          localStorage.setItem(keys.plays, JSON.stringify(remainingPlays));
          return anyFlushed;
        }
      }
      clearPlays();
    }

    // Offline play-cell EDITS (qb/st playlist). Each entry is already an
    // onSavePlays payload { game_id, plays }. Drained here so the app's OWN
    // reconnect/interval flush pushes them — previously only the dashboard's
    // sweep did, so edits made offline sat locally until the coach happened
    // to visit the dashboard. Runs before deletes so a queued delete of the
    // same play still wins.
    const pendingUpdated = safeRead<{ game_id?: string | number; plays?: unknown[] }>(
      keys.updatedPlays,
    );
    if (pendingUpdated.length > 0) {
      const remainingUpdated = [...pendingUpdated];
      for (const group of pendingUpdated) {
        const plays = Array.isArray(group?.plays) ? group.plays : [];
        if (!group?.game_id || plays.length === 0) {
          remainingUpdated.splice(remainingUpdated.indexOf(group), 1);
          continue;
        }
        try {
          await libraryServices.onSavePlays(
            { game_id: group.game_id, plays } as SavePlaysPayload,
            config,
          );
          remainingUpdated.splice(remainingUpdated.indexOf(group), 1);
          anyFlushed = true;
        } catch (err) {
          if (isPermanentRejection(err)) {
            remainingUpdated.splice(remainingUpdated.indexOf(group), 1);
            deadLetter("UpdatedPlays", group);
            continue;
          }
          console.error(`[${appKey}] Failed to sync updated plays:`, err);
          localStorage.setItem(
            keys.updatedPlays,
            JSON.stringify(remainingUpdated),
          );
          return anyFlushed;
        }
      }
      localStorage.removeItem(keys.updatedPlays);
    }

    // Deletes flush LAST: if an upsert of the same play was queued above it
    // lands first, then the delete wins — matching the user's final intent.
    const pendingDeletes = readDeletes();
    if (pendingDeletes.length > 0) {
      const remaining = [...pendingDeletes];
      for (const playId of pendingDeletes) {
        try {
          await libraryServices.onDeletePlay(playId, config);
          remaining.splice(remaining.indexOf(playId), 1);
          anyFlushed = true;
        } catch (err) {
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          // 404 = the play never reached (or already left) the server;
          // 403 = not ours. Neither can succeed by retrying — drop the
          // entry so it can't wedge the queue forever.
          if (status === 404 || status === 403) {
            remaining.splice(remaining.indexOf(playId), 1);
            anyFlushed = true;
            continue;
          }
          console.error(`[${appKey}] Failed to sync play delete:`, err);
          localStorage.setItem(keys.deletes, JSON.stringify(remaining));
          return anyFlushed;
        }
      }
      clearDeletes();
    }

    return anyFlushed;
  };

  return {
    keys,
    pushSettings: (s) => push(keys.settings, s),
    pushGame: (g) => push(keys.games, g),
    pushPlays: (p) => push(keys.plays, p),
    pushDelete,
    readSettings,
    readGames,
    readPlays,
    readDeletes,
    clearSettings,
    clearGames,
    clearPlays,
    clearDeletes,
    syncPending,
  };
}
